import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

const ACTION_FOOTER_GAP_PX = 0.5;

const getMainContentInset = () => {
  const main = document.querySelector('main');
  if (!main) {
    return { left: 0, right: 0 };
  }
  const rect = main.getBoundingClientRect();
  return {
    left: Math.max(0, rect.left),
    right: Math.max(0, window.innerWidth - rect.right),
  };
};

/** Height of the portal copyright bar (fixed, high z-index), not this component. */
const getSiteFooterHeight = () => {
  let height = 0;
  document.querySelectorAll('div').forEach((el) => {
    if (el.getAttribute('aria-label') === 'Prescription actions') {
      return;
    }
    const style = window.getComputedStyle(el);
    if (style.position !== 'fixed') {
      return;
    }
    if (parseFloat(style.bottom) > 1) {
      return;
    }
    const zIndex = parseInt(style.zIndex, 10);
    if (Number.isNaN(zIndex) || zIndex < 9000) {
      return;
    }
    height = Math.max(height, el.getBoundingClientRect().height);
  });
  return height || 40;
};

const getLayout = () => ({
  ...getMainContentInset(),
  bottom: getSiteFooterHeight() + ACTION_FOOTER_GAP_PX,
});

/** Page-level action bar (outside Paper); fixed above the portal copyright footer. */
const AssignPrescriptionActionFooter = ({ children }) => {
  const [layout, setLayout] = useState(getLayout);

  useLayoutEffect(() => {
    const sync = () => setLayout(getLayout());

    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, { passive: true });

    const main = document.querySelector('main');
    const Observer = window.ResizeObserver;
    const observer = main && Observer ? new Observer(sync) : null;
    if (main && observer) {
      observer.observe(main);
    }

    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <Box
      className="react-no-print"
      component="footer"
      aria-label="Prescription actions"
      sx={(theme) => ({
        position: 'fixed',
        bottom: layout.bottom,
        left: layout.left,
        right: layout.right,
        zIndex: theme.zIndex.appBar,
        boxSizing: 'border-box',
        px: { xs: 1, sm: 1.25 },
        py: { xs: 0.75, sm: 0.85 },
        borderTop: '1px solid #e2e8f0',
        borderRadius: 0,
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 -4px 20px rgba(15, 23, 42, 0.08)',
        paddingBottom: 'max(4px, env(safe-area-inset-bottom, 0px))',
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 0.75 },
          justifyContent: 'flex-start',
          alignItems: 'center',
          alignContent: 'flex-start',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

AssignPrescriptionActionFooter.propTypes = {
  children: PropTypes.node,
};

AssignPrescriptionActionFooter.defaultProps = {
  children: null,
};

export default AssignPrescriptionActionFooter;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/AssignPrescriptionActionFooter.jsx